import { useState, useEffect } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { Navigate } from 'react-router-dom';
import {
  getOverviewStats,
  getDailyStats,
  getCountryStats,
  getTopUsers,
  getAlerts,
  acknowledgeAlert,
  refreshDailyStats,
  getSystemStats,
} from '@/api/admin';
import type { OverviewStats, DailyStats, CountryStats, UserActivityStats, AnomalyAlert, SystemStats } from '@/types';
import { SystemMetricsChart } from '@/components/SystemMetricsChart';

type Tab = 'overview' | 'users' | 'content' | 'geography' | 'alerts' | 'system';

export function AdminPage() {
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Data states
  const [overview, setOverview] = useState<OverviewStats | null>(null);
  const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);
  const [countryStats, setCountryStats] = useState<CountryStats[]>([]);
  const [topUsers, setTopUsers] = useState<UserActivityStats[]>([]);
  const [alerts, setAlerts] = useState<AnomalyAlert[]>([]);
  const [systemStats, setSystemStats] = useState<SystemStats | null>(null);

  // Redirect non-admins
  if (!user?.isAdmin) {
    return <Navigate to="/" replace />;
  }

  useEffect(() => {
    loadData();
  }, [activeTab]);

  async function loadData() {
    setIsLoading(true);
    setError(null);
    try {
      switch (activeTab) {
        case 'overview':
          const [overviewData, recentStats] = await Promise.all([
            getOverviewStats(),
            getDailyStats({ limit: 7 }),
          ]);
          setOverview(overviewData);
          setDailyStats(recentStats);
          break;
        case 'users':
          const usersData = await getTopUsers(50);
          setTopUsers(usersData);
          break;
        case 'geography':
          const geoData = await getCountryStats(50);
          setCountryStats(geoData);
          break;
        case 'alerts':
          const alertsData = await getAlerts(50);
          setAlerts(alertsData);
          break;
        case 'content':
          const contentStats = await getDailyStats({ limit: 30 });
          setDailyStats(contentStats);
          break;
        case 'system':
          const sysData = await getSystemStats();
          setSystemStats(sysData);
          break;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }

  async function handleRefreshStats() {
    try {
      await refreshDailyStats();
      loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to refresh stats');
    }
  }

  async function handleAcknowledgeAlert(alertId: string) {
    try {
      const updated = await acknowledgeAlert(alertId);
      setAlerts(alerts.map(a => a.id === alertId ? updated : a));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge alert');
    }
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'users', label: 'Users' },
    { id: 'content', label: 'Content' },
    { id: 'geography', label: 'Geography' },
    { id: 'alerts', label: 'Alerts' },
    { id: 'system', label: 'System' },
  ];

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Admin Dashboard</h1>
        <button
          onClick={handleRefreshStats}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity"
        >
          Refresh Stats
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 p-4 bg-destructive/10 text-destructive rounded-lg">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : (
        <>
          {activeTab === 'overview' && <OverviewTab overview={overview} dailyStats={dailyStats} />}
          {activeTab === 'users' && <UsersTab users={topUsers} />}
          {activeTab === 'content' && <ContentTab dailyStats={dailyStats} />}
          {activeTab === 'geography' && <GeographyTab countryStats={countryStats} />}
          {activeTab === 'alerts' && <AlertsTab alerts={alerts} onAcknowledge={handleAcknowledgeAlert} />}
          {activeTab === 'system' && <SystemTab stats={systemStats} onRefresh={loadData} />}
        </>
      )}
    </div>
  );
}

function OverviewTab({ overview, dailyStats }: { overview: OverviewStats | null; dailyStats: DailyStats[] }) {
  if (!overview) return null;

  const statCards = [
    { label: 'Total Users', value: overview.totalUsers, today: overview.usersToday },
    { label: 'Total Blahblahs', value: overview.totalBlahblahs, today: overview.blahblahsToday },
    { label: 'Total Plays', value: overview.totalPlays },
    { label: 'Total Likes', value: overview.totalLikes },
    { label: 'Total Follows', value: overview.totalFollows },
    { label: 'Total Reports', value: overview.totalReports },
    { label: 'Active Users Today', value: overview.activeUsersToday },
    { label: 'Unique Countries', value: overview.uniqueCountries },
  ];

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {statCards.map(stat => (
          <div key={stat.label} className="bg-card p-4 rounded-lg border border-border">
            <div className="text-sm text-muted-foreground">{stat.label}</div>
            <div className="text-2xl font-bold">{stat.value.toLocaleString()}</div>
            {stat.today !== undefined && (
              <div className="text-xs text-green-500">+{stat.today} today</div>
            )}
          </div>
        ))}
      </div>

      {/* Recent Daily Stats */}
      <div className="bg-card p-4 rounded-lg border border-border">
        <h3 className="text-lg font-semibold mb-4">Last 7 Days</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left p-2">Date</th>
                <th className="text-right p-2">Users</th>
                <th className="text-right p-2">Active</th>
                <th className="text-right p-2">Blahblahs</th>
                <th className="text-right p-2">Plays</th>
                <th className="text-right p-2">Likes</th>
              </tr>
            </thead>
            <tbody>
              {dailyStats.map(day => (
                <tr key={day.date} className="border-b border-border/50">
                  <td className="p-2">{day.date}</td>
                  <td className="p-2 text-right">{day.newUsers}</td>
                  <td className="p-2 text-right">{day.activeUsers}</td>
                  <td className="p-2 text-right">{day.newBlahblahs}</td>
                  <td className="p-2 text-right">{day.totalPlays}</td>
                  <td className="p-2 text-right">{day.totalLikes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function UsersTab({ users }: { users: UserActivityStats[] }) {
  return (
    <div className="bg-card p-4 rounded-lg border border-border">
      <h3 className="text-lg font-semibold mb-4">Top Users by Activity</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-2">User</th>
              <th className="text-right p-2">Blahblahs</th>
              <th className="text-right p-2">Likes</th>
              <th className="text-right p-2">Views</th>
              <th className="text-right p-2">Followers</th>
              <th className="text-left p-2">Last Active</th>
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.userId} className="border-b border-border/50">
                <td className="p-2">
                  <div className="font-medium">{user.displayName}</div>
                  {user.email && <div className="text-xs text-muted-foreground">{user.email}</div>}
                </td>
                <td className="p-2 text-right">{user.blahblahCount}</td>
                <td className="p-2 text-right">{user.totalLikesReceived}</td>
                <td className="p-2 text-right">{user.totalViewsReceived}</td>
                <td className="p-2 text-right">{user.followerCount}</td>
                <td className="p-2 text-xs text-muted-foreground">
                  {user.lastActive ? new Date(user.lastActive).toLocaleDateString() : 'Never'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ContentTab({ dailyStats }: { dailyStats: DailyStats[] }) {
  return (
    <div className="bg-card p-4 rounded-lg border border-border">
      <h3 className="text-lg font-semibold mb-4">Content Stats (Last 30 Days)</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-2">Date</th>
              <th className="text-right p-2">Blahblahs</th>
              <th className="text-right p-2">Replies</th>
              <th className="text-right p-2">Plays</th>
              <th className="text-right p-2">Likes</th>
              <th className="text-right p-2">Reports</th>
            </tr>
          </thead>
          <tbody>
            {dailyStats.map(day => (
              <tr key={day.date} className="border-b border-border/50">
                <td className="p-2">{day.date}</td>
                <td className="p-2 text-right">{day.newBlahblahs}</td>
                <td className="p-2 text-right">{day.totalReplies}</td>
                <td className="p-2 text-right">{day.totalPlays}</td>
                <td className="p-2 text-right">{day.totalLikes}</td>
                <td className="p-2 text-right">{day.totalReports}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function GeographyTab({ countryStats }: { countryStats: CountryStats[] }) {
  return (
    <div className="bg-card p-4 rounded-lg border border-border">
      <h3 className="text-lg font-semibold mb-4">User Distribution by Country</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-2">Country</th>
              <th className="text-right p-2">Users</th>
              <th className="text-right p-2">Sessions</th>
            </tr>
          </thead>
          <tbody>
            {countryStats.map(country => (
              <tr key={country.countryCode} className="border-b border-border/50">
                <td className="p-2">
                  <span className="mr-2">{getCountryFlag(country.countryCode)}</span>
                  {country.countryCode}
                </td>
                <td className="p-2 text-right">{country.userCount}</td>
                <td className="p-2 text-right">{country.sessionCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AlertsTab({ alerts, onAcknowledge }: { alerts: AnomalyAlert[]; onAcknowledge: (id: string) => void }) {
  const unacknowledged = alerts.filter(a => !a.acknowledged);
  const acknowledged = alerts.filter(a => a.acknowledged);

  return (
    <div className="space-y-6">
      {/* Unacknowledged Alerts */}
      <div className="bg-card p-4 rounded-lg border border-border">
        <h3 className="text-lg font-semibold mb-4">Active Alerts ({unacknowledged.length})</h3>
        {unacknowledged.length === 0 ? (
          <p className="text-muted-foreground">No active alerts</p>
        ) : (
          <div className="space-y-3">
            {unacknowledged.map(alert => (
              <div
                key={alert.id}
                className={`p-4 rounded-lg border ${
                  alert.severity === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                  alert.severity === 'warning' ? 'bg-yellow-500/10 border-yellow-500/30' :
                  'bg-blue-500/10 border-blue-500/30'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`px-2 py-0.5 text-xs rounded ${
                        alert.severity === 'critical' ? 'bg-red-500 text-white' :
                        alert.severity === 'warning' ? 'bg-yellow-500 text-black' :
                        'bg-blue-500 text-white'
                      }`}>
                        {alert.severity.toUpperCase()}
                      </span>
                      <span className="text-sm font-medium">{alert.alertType}</span>
                    </div>
                    <p className="text-sm">{alert.message}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {new Date(alert.createdAt).toLocaleString()}
                    </p>
                  </div>
                  <button
                    onClick={() => onAcknowledge(alert.id)}
                    className="px-3 py-1 text-sm bg-primary text-primary-foreground rounded hover:opacity-90"
                  >
                    Acknowledge
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Acknowledged Alerts */}
      {acknowledged.length > 0 && (
        <div className="bg-card p-4 rounded-lg border border-border">
          <h3 className="text-lg font-semibold mb-4">Acknowledged Alerts ({acknowledged.length})</h3>
          <div className="space-y-2">
            {acknowledged.map(alert => (
              <div key={alert.id} className="p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs text-muted-foreground">{alert.alertType}</span>
                </div>
                <p className="text-sm text-muted-foreground">{alert.message}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Acknowledged {alert.acknowledgedAt ? new Date(alert.acknowledgedAt).toLocaleString() : ''}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SystemTab({ stats, onRefresh }: { stats: SystemStats | null; onRefresh: () => void }) {
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    if (!autoRefresh) return;
    const id = setInterval(onRefresh, 5000);
    return () => clearInterval(id);
  }, [autoRefresh, onRefresh]);

  return (
    <div className="space-y-6">
      {/* Header with auto-refresh toggle */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted-foreground">
          {stats?.version && <>Version: <span className="font-mono">{stats.version}</span></>}
        </div>
        <button
          onClick={() => setAutoRefresh(!autoRefresh)}
          className={`px-3 py-1.5 rounded text-sm transition-colors ${
            autoRefresh ? 'bg-green-500/20 text-green-500' : 'bg-muted text-muted-foreground'
          }`}
        >
          Auto-refresh {autoRefresh ? 'ON' : 'OFF'}
        </button>
      </div>

      {/* Instant snapshot cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <MetricCard
            label="CPU Usage"
            value={`${stats.cpuUsagePercent.toFixed(1)}%`}
            sub={`${stats.cpuCount} cores`}
            percent={stats.cpuUsagePercent}
          />
          <MetricCard
            label="Memory Usage"
            value={`${stats.memoryUsagePercent.toFixed(1)}%`}
            sub={`${formatBytes(stats.memoryUsedBytes)} / ${formatBytes(stats.memoryTotalBytes)}`}
            percent={stats.memoryUsagePercent}
          />
          <MetricCard
            label="Disk Usage"
            value={`${stats.diskUsagePercent.toFixed(1)}%`}
            sub={`${formatBytes(stats.diskUsedBytes)} / ${formatBytes(stats.diskTotalBytes)}`}
            percent={stats.diskUsagePercent}
          />
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-card p-4 rounded-lg border border-border">
            <div className="text-sm text-muted-foreground mb-2">Server Info</div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Uptime</span><span className="font-mono">{formatUptime(stats.uptimeSeconds)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Requests</span><span className="font-mono">{stats.totalRequests.toLocaleString()}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Version</span><span className="font-mono">{stats.version}</span></div>
            </div>
          </div>
          <div className="bg-card p-4 rounded-lg border border-border">
            <div className="text-sm text-muted-foreground mb-2">Database Pool</div>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">Pool Size</span><span className="font-mono">{stats.dbPoolSize}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Idle</span><span className="font-mono">{stats.dbPoolIdle}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">Active</span><span className="font-mono">{stats.dbPoolActive}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* Time-series charts */}
      <div>
        <h3 className="text-lg font-semibold mb-4">History</h3>
        <SystemMetricsChart />
      </div>
    </div>
  );
}

function MetricCard({ label, value, sub, percent }: { label: string; value: string; sub: string; percent: number }) {
  const color = percent > 90 ? 'text-red-500' : percent > 70 ? 'text-yellow-500' : 'text-green-500';
  const barColor = percent > 90 ? 'bg-red-500' : percent > 70 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className="bg-card p-4 rounded-lg border border-border">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-xs text-muted-foreground mb-2">{sub}</div>
      <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${Math.min(percent, 100)}%` }} />
      </div>
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

// Helper to get country flag emoji from country code
function getCountryFlag(countryCode: string): string {
  if (!countryCode || countryCode.length !== 2) return '';
  const offset = 127397;
  return String.fromCodePoint(
    ...countryCode.toUpperCase().split('').map(char => char.charCodeAt(0) + offset)
  );
}

export default AdminPage;
